import {
  Column,
  Copy,
  Flex,
  Heading,
  Loader,
  Row,
  Square,
  Stack,
  SvgInfo,
} from '@grubhubprod/gh-cookbook-react/dist';
import { addDays, format as formatDate } from 'date-fns';
import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { RangeModifier } from 'react-day-picker/types/Modifiers';
import { UseFormMethods } from 'react-hook-form/dist/index.ie11';
import { useMealCreditEntityName } from 'src/components/selfService/common/hooks/useMealCreditEntityName';
import { Checkbox } from 'src/components/Checkbox/Checkbox';
import {
  DatePickerInput,
  DATE_FORMAT,
} from 'src/components/DatePickerInput/DatePickerInput';
import { Input } from 'src/components/Input/Input';
import { Select } from 'src/components/Select/Select';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { ILocFormFields } from 'src/models/LineOfCreditModelUtils';
import { CurrencyInput } from 'src/components/CurrencyInput/CurrencyInput';
import { DayPills } from 'src/components/DayPills/DayPills';

interface IFormSectionProps {
  hideHeader?: boolean;
  updateMobileStepSize?: () => void;
  useDatePickerModal?: boolean;
  style: CSSProperties;
  isEditing?: boolean;
  values?: ILocFormFields;
  notifyFormValidity?: (boolean) => void;
  formMethods: UseFormMethods<ILocFormFields>;
  defaultDays: (string | boolean | number)[];
  isDataLoading: boolean;
  forceMonthsToWrap?: boolean;
  isSignup?: boolean;
}

const DESKTOP_MARGIN = 'md';
const DATE_PLACEHOLDER = DATE_FORMAT.toUpperCase();

export function LineOfCreditForm({
  hideHeader,
  style,
  updateMobileStepSize,
  useDatePickerModal,
  isEditing,
  values,
  notifyFormValidity,
  formMethods,
  defaultDays,
  isDataLoading,
  forceMonthsToWrap,
  isSignup,
}: IFormSectionProps): JSX.Element {
  const { MEAL_CREDIT_ENTITY_NAME } = useMealCreditEntityName();

  const {
    formState,
    register,
    control,
    errors,
    watch,
    unregister,
    trigger,
    setValue,
  } = formMethods;

  const { isValid } = formState;

  const [initDateRange, setInitDateRange] = useState(undefined);
  const [initDate, setInitDate] = useState(undefined);

  const previousErrors = useRef({});

  const selectedDays = watch('days', defaultDays);
  const budgetLength = watch('budgetLength', '');
  const frequency = watch('frequency');
  const date = watch('date');

  const isRepeatingBudget = frequency === 'repeats';
  const isDayLengthBudget = budgetLength === 'day';
  const isWeekLengthBudget = budgetLength === 'week';

  const previousBudgetFrequency = useRef('');
  const previousBudgetLength = useRef('');
  const editingDisabled = isSignup ? false : isEditing;

  function getDateInputLabel(): string {
    if (isRepeatingBudget) {
      return 'Starts on';
    }

    if (isDayLengthBudget) {
      return 'Date';
    }

    if (isWeekLengthBudget) {
      return 'Dates';
    }

    return '';
  }

  useEffect(() => {
    register({ name: 'date' }, { required: 'Date is required' });

    return () => unregister('date');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isEditing && values) {
      const days = (values.days as number[]).reduce(
        (selectedDays, dayIndex) => {
          // eslint-disable-next-line no-param-reassign
          selectedDays[dayIndex] = dayIndex;
          return selectedDays;
        },
        defaultDays
      );
      setValue('creditName', values.creditName, { shouldValidate: true });
      setValue('amount', values.amount, { shouldValidate: true });
      setValue('budgetLength', values.budgetLength, {
        shouldValidate: true,
      });

      previousBudgetFrequency.current = values.frequency;
      previousBudgetLength.current = values.budgetLength;

      setValue('days', days, { shouldValidate: true });

      // ensures it is not updated before the register
      setTimeout(() => {
        setValue('date', values.date, { shouldValidate: true });
        setValue('frequency', values.frequency, { shouldValidate: true });
      }, 0);

      if (values.budgetLength === 'week') {
        setInitDateRange(values.date as RangeModifier);
      } else {
        setInitDate(values.date as Date);
      }

      setValue('isAdminIncludedInBudget', values.isAdminIncludedInBudget);
    } else {
      resetDaysSelection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, values, defaultDays]);

  // maxHeight of step on signup needs to be updated
  // when conditional elements are rendered
  useEffect(() => {
    if (updateMobileStepSize) {
      updateMobileStepSize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budgetLength, frequency]);

  // clear out budget specifics if the budget length changes
  useEffect(() => {
    setValue('frequency', 'single', { shouldValidate: true });
    updateDateValue(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budgetLength]);

  useEffect(() => {
    if (isDayLengthBudget) {
      setInitDateRange({ from: null, to: null });
      /**
       * If LOC is being changed from:
       *   1 DAY single to 1 DAY repeat
       *   1 WEEK to 1 DAY repeat
       * clean up days selections otherwise it shows all days selected as they come from API
       */
      if (
        isRepeatingBudget &&
        (previousBudgetFrequency.current === 'single' ||
          previousBudgetLength.current === 'week')
      ) {
        resetDaysSelection();
      }
    }
    if (isWeekLengthBudget) {
      setInitDate(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDayLengthBudget, isWeekLengthBudget, isRepeatingBudget]);

  // maxHeight of step on signup needs to be updated
  // when conditional form errors are rendered
  useEffect(() => {
    if (
      updateMobileStepSize &&
      errors &&
      previousErrors.current &&
      Object.keys(errors).length !== Object.keys(previousErrors.current).length
    ) {
      updateMobileStepSize();
      previousErrors.current = { ...errors };
    }
  });

  useEffect(() => {
    let hasValidSelectedDays = true;

    if (budgetLength === 'day' && frequency === 'repeats') {
      hasValidSelectedDays = selectedDays.some((day) => day !== false);
    }

    if (notifyFormValidity) {
      notifyFormValidity(isValid && hasValidSelectedDays);
    }
  });

  function resetDaysSelection() {
    defaultDays.reduce((selectedDays, dayIndex) => {
      // eslint-disable-next-line no-param-reassign
      selectedDays[dayIndex as number] = false;
      return selectedDays;
    }, defaultDays);
    setValue('days', defaultDays, { shouldValidate: true });
  }

  function updateDateValue(value: Date | RangeModifier): void {
    setValue('date', value, { shouldValidate: true });
  }

  function getBudgetRefreshCopy(): string {
    const dateRange = date as RangeModifier;
    if (dateRange && dateRange.to) {
      return `This credit will refresh on ${getBudgetRefreshDate()} and remain active until you delete it.`;
    }

    return '';
  }

  function getBudgetRefreshDate(): string {
    const { to: endDate } = date as RangeModifier;
    const refreshDate = addDays(endDate, 1);

    return formatDate(refreshDate, DATE_FORMAT);
  }

  return (
    <div
      style={{
        ...style,
        position: 'relative',
      }}
    >
      {isDataLoading && (
        <Flex
          alignItems="center"
          justifyContent="center"
          backgroundColor="globals.white75a"
          position="absolute"
          width="100%"
          height="100%"
          zIndex={10}
        >
          <Loader size="md" />
        </Flex>
      )}
      {!hideHeader && (
        <>
          <Stack y="xxx-sm">
            <Heading variant="h2">{`Create your ${MEAL_CREDIT_ENTITY_NAME.singular}`}</Heading>
          </Stack>

          <Stack y={DESKTOP_MARGIN}>
            <Copy variant="body">
              Feed your next virtual event or give your team the lunch break
              they deserve
            </Copy>
          </Stack>
        </>
      )}
      <form>
        <Stack y={DESKTOP_MARGIN}>
          <Row gutter={0}>
            <Column gutter={0}>
              <Input
                label="Credit name"
                name="creditName"
                id="creditName"
                placeholder="(e.g. Weekly lunch, Happy hour)"
                ref={register({
                  required: 'Credit name is required',
                  maxLength: {
                    value: 30,
                    message: 'Credit name must be 30 characters or less',
                  },
                })}
                error={errors.creditName}
              />
            </Column>
          </Row>
        </Stack>
        <Stack y={DESKTOP_MARGIN}>
          <Row gutter={12}>
            <Column gutter={12} desktop={6}>
              <CurrencyInput
                id="amount"
                name="amount"
                required
                error={errors.amount}
                control={control}
                label="Amount per person"
                errorMessages={{
                  required: 'Amount per person is required',
                  pattern: 'Invalid amount',
                }}
              />
            </Column>
          </Row>
        </Stack>
        <Stack y={DESKTOP_MARGIN}>
          <Row gutter={12}>
            <Column gutter={12} desktop={6}>
              <Select
                label={
                  <>
                    <Stack as="span" x="xxx-sm">
                      How long do people have to spend this credit?
                    </Stack>
                    <Tooltip
                      maxWidth="290px"
                      content={
                        <ul
                          data-testid="budget-length-tooltip"
                          className="lineOfCreditStep_tooltipList"
                        >
                          <li>
                            If you select 1 day, the credit will be available
                            from 12am to 11:59pm local time on the date you
                            selected
                          </li>
                          <li>
                            If you choose 1 week, the credit will be available
                            for a whole week starting on the date you selected
                          </li>
                        </ul>
                      }
                    >
                      <span
                        data-testid="budget-length-tooltip-icon"
                        className="lineOfCreditStep_tooltipIcon"
                      >
                        <SvgInfo />
                      </span>
                    </Tooltip>
                  </>
                }
                disabled={editingDisabled}
                name="budgetLength"
                id="budgetLength"
                ref={register({
                  required: 'Please select an option',
                })}
                error={errors.budgetLength}
              >
                {!frequency && <option value=""> </option>}
                <option value="day">1 day</option>
                <option value="week">1 week</option>
              </Select>
            </Column>
          </Row>
        </Stack>

        {isSignup && (
          <Stack y={DESKTOP_MARGIN}>
            <Row gutter={12}>
              <Column gutter={12} desktop={12}>
                <Stack as="span" x="xxx-sm">
                  <Checkbox
                    id="isAdminIncludedInBudget"
                    name="isAdminIncludedInBudget"
                    label={`The admin can spend this ${MEAL_CREDIT_ENTITY_NAME.singular}`}
                    ref={register}
                  />
                </Stack>
                <Tooltip
                  maxWidth="290px"
                  content={
                    <Copy variant="body" color="invertedPrimaryText">
                      You are the admin because you're creating the account. By
                      selecting this box, you'll grant yourself access to the
                      {MEAL_CREDIT_ENTITY_NAME.singular}. You can edit this
                      setting later in your account by removing the admin from
                      this {MEAL_CREDIT_ENTITY_NAME.singular}.
                    </Copy>
                  }
                >
                  <span
                    data-testid="budget-length-tooltip-icon"
                    className="lineOfCreditStep_tooltipIcon"
                  >
                    <SvgInfo />
                  </span>
                </Tooltip>
              </Column>
            </Row>
          </Stack>
        )}

        {!!budgetLength && (
          <Square
            inset="lg"
            style={{
              paddingLeft: 0,
              paddingRight: 0,
            }}
            data-testid="budget-frequency-section"
            borderTop="1px solid"
            borderColor="lightLine"
          >
            <Row gutter={12}>
              <Column gutter={12} desktop={6}>
                <Stack y={DESKTOP_MARGIN}>
                  <DatePickerInput
                    disabled={editingDisabled}
                    forceMonthsToWrap={forceMonthsToWrap}
                    initialDateRange={initDateRange}
                    initialDate={initDate}
                    dayPickerProps={{
                      disabledDays: {
                        before: new Date(),
                      },
                      numberOfMonths: isWeekLengthBudget ? 2 : 1,
                    }}
                    chooseConsecutiveDays={isWeekLengthBudget}
                    useFullScreenOverlay={useDatePickerModal}
                    placeholder={
                      isDayLengthBudget
                        ? DATE_PLACEHOLDER
                        : `${DATE_PLACEHOLDER}-${DATE_PLACEHOLDER}`
                    }
                    label={getDateInputLabel()}
                    name="date"
                    id="date"
                    onBlur={() => {
                      trigger('date');
                    }}
                    onDateChange={(startDate: Date, endDate: Date) => {
                      if (endDate) {
                        updateDateValue({
                          from: startDate,
                          to: endDate,
                        });
                      } else {
                        updateDateValue(startDate);
                      }
                    }}
                  />
                </Stack>
              </Column>

              <Column gutter={12} desktop={6}>
                <Stack y={DESKTOP_MARGIN}>
                  <Select
                    label={
                      <>
                        <Stack as="span" x="xxx-sm">
                          Frequency
                        </Stack>
                        <Tooltip
                          maxWidth="290px"
                          content={
                            <>
                              <Stack y="sm">
                                <ul
                                  data-testid="budget-length-tooltip"
                                  className="lineOfCreditStep_tooltipList"
                                >
                                  <li>
                                    If the {MEAL_CREDIT_ENTITY_NAME.singular}{' '}
                                    repeats daily, it will be available for the
                                    whole day (12am to 11:59pm local time) on
                                    the day(s) you selected
                                  </li>
                                  <li>
                                    If the {MEAL_CREDIT_ENTITY_NAME.singular}{' '}
                                    repeats weekly, it will be available for a
                                    whole week starting on the date you selected
                                    and will refresh after the 7th day
                                  </li>
                                </ul>
                              </Stack>
                              <Copy variant="body" color="invertedPrimaryText">
                                You can delete a{' '}
                                {MEAL_CREDIT_ENTITY_NAME.singular} at any time
                                from your business account
                              </Copy>
                            </>
                          }
                        >
                          <span
                            data-testid="budget-length-tooltip-icon"
                            className="lineOfCreditStep_tooltipIcon"
                          >
                            <SvgInfo />
                          </span>
                        </Tooltip>
                      </>
                    }
                    name="frequency"
                    id="frequency"
                    ref={register}
                    disabled={editingDisabled}
                  >
                    <option key="single" value="single">
                      Does not repeat
                    </option>
                    <option key="repeats" value="repeats">
                      {isDayLengthBudget
                        ? 'Customize frequency'
                        : 'Repeats every week'}
                    </option>
                  </Select>
                </Stack>
              </Column>
            </Row>
            {isDayLengthBudget && isRepeatingBudget && (
              <>
                <Stack y="x-sm">
                  <Copy variant="body">
                    On which day(s) does this credit repeat?
                  </Copy>
                </Stack>
                <Stack y="sm">
                  <Row gutter={8}>
                    <DayPills selectedDays={selectedDays} register={register} />
                  </Row>
                </Stack>
                <Copy variant="caption" color="secondaryText">
                  This {MEAL_CREDIT_ENTITY_NAME.singular} will remain active
                  until you make changes in your account.
                </Copy>
              </>
            )}
            {isWeekLengthBudget && isRepeatingBudget && (
              <Copy variant="caption" color="secondaryText">
                {getBudgetRefreshCopy()}
              </Copy>
            )}
            <div>
              <Copy variant="caption" color="secondaryText">
                Make sure to add people to this credit so they can start
                ordering.
              </Copy>
            </div>
          </Square>
        )}
      </form>
    </div>
  );
}
